// fallow-ignore-file complexity
// Pure task-filtering / classification helpers extracted from src/pages/Tasks.tsx (TES-849).
// Behavior-preserving: the logic here is byte-for-byte the same as the former inline
// implementation in Tasks.tsx — only relocated for testability + lower page complexity.
import { startOfWeek, endOfWeek, isWithinInterval, parseISO, isValid } from "date-fns";
import { isInspectionOverdue, isDefectOverdue } from "@/lib/dateUtils";
import type { UnifiedTask, TaskType } from "@/hooks/useTasksQuery";

export interface TaskStatusFlags {
  isCompleted: boolean;
  isInProgress: boolean;
  isAwaitingVerification: boolean;
}

// Re-exported for the characterization test net (TES-849); production consumers use the
// aggregate helpers (filterTasks / computeTaskStats / getUnavailableAssigneeTasks) below.
// fallow-ignore-next-line unused-export
export const defectStatusMap: Record<string, TaskStatusFlags> = {
  open: { isCompleted: false, isInProgress: false, isAwaitingVerification: false },
  in_progress: { isCompleted: false, isInProgress: true, isAwaitingVerification: false },
  resolved: { isCompleted: false, isInProgress: false, isAwaitingVerification: true }, // Gelöst, aber noch zu verifizieren
  closed: { isCompleted: true, isInProgress: false, isAwaitingVerification: false },
};

// fallow-ignore-next-line unused-export
export const inspectionStatusMap: Record<string, TaskStatusFlags> = {
  pending: { isCompleted: false, isInProgress: false, isAwaitingVerification: false },
  in_progress: { isCompleted: false, isInProgress: true, isAwaitingVerification: false },
  completed: { isCompleted: true, isInProgress: false, isAwaitingVerification: false },
  overdue: { isCompleted: false, isInProgress: false, isAwaitingVerification: false },
};

const NEUTRAL_STATUS: TaskStatusFlags = {
  isCompleted: false,
  isInProgress: false,
  isAwaitingVerification: false,
};

// fallow-ignore-next-line unused-export
export function getTaskStatus(task: UnifiedTask): TaskStatusFlags {
  if (
    task.type === "defect" ||
    task.type === "unassigned_defect" ||
    task.type === "unavailable_defect" ||
    task.type === "group_unassigned_defect" ||
    // TES-2058 ([R12]): qm_admin-Oversight-Mangel — gleicher Status-Enum wie "defect", nur ohne
    // Zuweisungs-Filter. Ohne diesen Zweig fiele der Task auf den inspectionStatusMap-Fallback
    // (falsche Statuswerte → immer NEUTRAL_STATUS, Badge/Tab-Zuordnung wäre falsch).
    task.type === "oversight_defect"
  ) {
    return defectStatusMap[task.status] || NEUTRAL_STATUS;
  }
  if (task.type === "verification") {
    return { isCompleted: false, isInProgress: false, isAwaitingVerification: true }; // Verification tasks are awaiting verification
  }
  // TES-2053: zu-verifizierende Prüfungen sind — wie die Mangel-Verifikationen — "zu verifizieren",
  // nicht "erledigt" (der rohe inspections-Status ist completed und würde sonst als isCompleted zählen).
  if (task.type === "inspection_verification") {
    return { isCompleted: false, isInProgress: false, isAwaitingVerification: true };
  }
  return inspectionStatusMap[task.status] || NEUTRAL_STATUS;
}

// fallow-ignore-next-line unused-export
export function isTaskOverdue(task: UnifiedTask): boolean {
  // Use centralized overdue check functions for all task types
  // Verification tasks use 'resolved' status which is now included in isDefectOverdue
  if (
    task.type === "defect" ||
    task.type === "verification" ||
    task.type === "unassigned_defect" ||
    task.type === "unavailable_defect" ||
    task.type === "group_unassigned_defect" ||
    // TES-2058 ([R12]): qm_admin-Oversight-Mangel — Mangel-Overdue-Regeln, nicht Prüfungs-Regeln.
    task.type === "oversight_defect"
  ) {
    return isDefectOverdue(task.due_date, task.status);
  }

  // For inspections (including unassigned and unavailable)
  return isInspectionOverdue(task.due_date, task.status);
}

// fallow-ignore-next-line unused-export
export function getSearchableText(task: UnifiedTask): string {
  if (
    (task.type === "defect" ||
      task.type === "verification" ||
      task.type === "unassigned_defect" ||
      task.type === "unavailable_defect" ||
      task.type === "group_unassigned_defect" ||
      // TES-2058 ([R12]): qm_admin-Oversight-Mangel bleibt durchsuchbar wie ein regulärer Mangel.
      task.type === "oversight_defect") &&
    task.defectData
  ) {
    return `${task.defectData.title} ${task.defectData.description || ""}`.toLowerCase();
  }
  if (
    (task.type === "inspection" ||
      task.type === "unassigned_inspection" ||
      task.type === "unavailable_inspection" ||
      task.type === "group_unassigned_inspection" ||
      task.type === "inspection_verification" ||
      // TES-2058 ([R12]): qm_admin-Oversight-Prüfung bleibt durchsuchbar wie eine reguläre Prüfung.
      task.type === "oversight_inspection") &&
    task.inspectionData
  ) {
    return `${task.inspectionData.checklists?.name || ""} ${task.inspectionData.inspection_positions?.name || ""}`.toLowerCase();
  }
  // TES-1869 (L6-3): erweiterte "Meine Aufgaben"-Typen.
  if (task.type === "inspection_point" && task.inspectionPointData) {
    return (task.inspectionPointData.checklist_item_title || "").toLowerCase();
  }
  if (task.type === "pt_action" && task.ptActionData) {
    return `${task.ptActionData.title} ${task.ptActionData.description || ""}`.toLowerCase();
  }
  // TES-2076 (D-UI): "Aufgaben als Stellvertreter" — auf Titel des vertretenen Items suchbar.
  if (task.type === "deputy" && task.deputyData) {
    return (task.deputyData.title || "").toLowerCase();
  }
  return "";
}

// Check if a task has an unavailable assignee
// fallow-ignore-next-line unused-export
export function isAssigneeUnavailable(task: UnifiedTask): boolean {
  if (task.type === "defect" && task.defectData) {
    return task.defectData.isAssigneeUnavailable === true;
  }
  if (task.type === "inspection" && task.inspectionData) {
    return task.inspectionData.isAssigneeUnavailable === true;
  }
  if (task.type === "verification" && task.defectData) {
    return task.defectData.isAssigneeUnavailable === true;
  }
  return false;
}

// Helper to check if type is an "extra" type (not surfaced in the default "all" tab / stats).
// inspection_point + pt_action (L6-3, TES-1869) sind hier gelistet: useTasksQuery vereinheitlicht
// ihre DATEN bereits, aber Tabs/Rendering (eigener Tab + Card) kommen erst mit der UI TES-1882.
// Bis dahin als "extra" behandeln, damit sie weder die Stats verfälschen noch im Renderer auf
// `return null` fallen — gleicher Lebenszyklus wie group_unassigned_defect (Daten da, Tab folgt).
// fallow-ignore-next-line unused-export
export function isExtraType(type: TaskType): boolean {
  return (
    type === "unassigned_defect" ||
    type === "unassigned_inspection" ||
    type === "unavailable_defect" ||
    type === "unavailable_inspection" ||
    type === "group_unassigned_defect" ||
    type === "group_unassigned_inspection" ||
    type === "inspection_point" ||
    type === "pt_action" ||
    // TES-2076 (D-UI): Deputy-Tasks sind eine getrennte Sicht ("Als Stellvertreter"), NICHT die
    // eigenen zugewiesenen Aufgaben des Callers → zählen (wie group_unassigned/pointsActions) NICHT
    // in die Primär-Zähler (total/openAll/…). Eigener KPI/Tab-Zähler: stats.deputy.
    type === "deputy"
  );
}

// TES-1994: cancelled hatte KEIN Mapping in den Status-Maps und fiel auf NEUTRAL zurück —
// stornierte Aufgaben zählten dadurch als "nicht begonnen/offen" (Badge < Seite). Der Fetch
// (useTasksQuery) filtert cancelled jetzt an der Quelle; dieser Guard ist die Defense-in-Depth
// für alle anderen Zulieferwege (z.B. künftige Task-Typen ohne Status-Mapping).
// fallow-ignore-next-line unused-export
export function isCountableTask(task: UnifiedTask): boolean {
  return !isExtraType(task.type) && task.status !== "cancelled";
}

// Per-tab membership predicates (search-independent). Each returns whether a task belongs
// to that tab; the uniform search filter is applied separately in filterTasks. This is a
// 1:1 decomposition of the former inline if-chain — each predicate reproduces exactly the
// non-`matchesSearch` part of its former branch (plus the TES-1994 cancelled guard).
const TAB_PREDICATES: Record<string, (task: UnifiedTask, status: TaskStatusFlags) => boolean> = {
  all: (task) => isCountableTask(task),
  openAll: (task, status) =>
    isCountableTask(task) &&
    // Offen = nicht begonnen + in Bearbeitung + zu verifizieren (nur Mängel)
    ((!status.isCompleted && !status.isInProgress && !status.isAwaitingVerification) ||
      status.isInProgress ||
      status.isAwaitingVerification),
  notStarted: (task, status) =>
    !status.isCompleted &&
    !status.isInProgress &&
    !status.isAwaitingVerification &&
    task.type !== "verification" &&
    isCountableTask(task),
  in_progress: (task, status) => status.isInProgress && isCountableTask(task),
  verification: (_task, status) => status.isAwaitingVerification,
  overdue: (task) => isTaskOverdue(task) && isCountableTask(task),
  completed: (task, status) => status.isCompleted && isCountableTask(task),
  // Copilot #773: auch die Type-Tabs durch isCountableTask — cancelled darf nicht via
  // Typ-Match wieder auftauchen, falls eine Task-Quelle den Fetch-Filter umgeht.
  // TES-2058 ([R12]): der "Mängel"/"Prüfungen"-Typ-Tab zählt für qm_admin auch die Oversight-Zeilen mit
  // (fachlich sind es Mängel/Prüfungen, nur ohne Zuweisungs-Filter) — sonst wäre der Tab für qm_admin
  // immer leer, obwohl "Meine Aufgaben" welche zeigt (Badge != Liste).
  defects: (task) =>
    (task.type === "defect" || task.type === "oversight_defect") && isCountableTask(task),
  inspections: (task) =>
    (task.type === "inspection" || task.type === "oversight_inspection") && isCountableTask(task),
  unassigned: (task) => task.type === "unassigned_defect" || task.type === "unassigned_inspection",
  // TES-1863 / TES-1870 "Meine Gruppen" Gruppen-Queue (Rename TES-2217): ownerlose Mängel + Prüfungen der eigenen Gruppen.
  groupUnassigned: (task) =>
    task.type === "group_unassigned_defect" || task.type === "group_unassigned_inspection",
  // TES-1869 (L6-3) "Meine Aufgaben" erweitert: zugewiesene Prüfpunkte + PT-TSK-Maßnahmen.
  pointsActions: (task) => task.type === "inspection_point" || task.type === "pt_action",
  // TES-2053 (LH §L6.3): eigene Sektion "Zu verifizieren (Prüfungen)" — abgeschlossene Prüfungen,
  // deren Verifikation beim Caller (Verifizierer/Deputy) liegt. Zusätzlich zählen sie über
  // isAwaitingVerification in den generischen "Zu verifizieren"-Tab (Parität zur Mangel-Verifikation).
  inspectionVerification: (task) => task.type === "inspection_verification",
  // TES-2076 (D-UI · LH §L6.3): eigene Sektion "Aufgaben als Stellvertreter" — die Union über die drei
  // Deputy-Ebenen (my_deputy_tasks, Mig 387). Badge == Listenlänge (Konsistenz, vgl. TES-2058): der
  // Tab-Predicate deckt sich 1:1 mit dem Stats-Zähler `deputy` (beide reiner Typ-Match, kein Status-Gate).
  deputy: (task) => task.type === "deputy",
  unavailableAssignee: (task) => {
    // Show unavailable_defect and unavailable_inspection types, plus own tasks with unavailable assignees
    if (task.type === "unavailable_defect" || task.type === "unavailable_inspection") {
      return true;
    }
    // Also include own tasks that have unavailable assignees
    if (task.type === "unassigned_defect" || task.type === "unassigned_inspection") return false;
    return isAssigneeUnavailable(task);
  },
};

/**
 * Filter the unified task list for a given active tab + search query.
 * Mirrors the former inline `filteredTasks` derivation in Tasks.tsx 1:1.
 */
export function filterTasks(
  tasks: UnifiedTask[],
  activeTab: string,
  searchQuery: string,
): UnifiedTask[] {
  const query = searchQuery.toLowerCase();
  // Unknown tabs fall through to a search-only filter, matching the former `return matchesSearch`.
  const predicate = TAB_PREDICATES[activeTab];
  return tasks.filter((task) => {
    const matchesSearch = getSearchableText(task).includes(query);
    return matchesSearch && (predicate ? predicate(task, getTaskStatus(task)) : true);
  });
}

export interface TaskStats {
  total: number;
  openAll: number;
  notStarted: number;
  in_progress: number;
  verification: number;
  overdue: number;
  completed: number;
  defects: number;
  inspections: number;
  unassigned: number;
  groupUnassigned: number;
  pointsActions: number;
  // TES-2053: eigene Sektion "Zu verifizieren (Prüfungen)".
  inspectionVerification: number;
  // TES-2076: eigene Sektion "Aufgaben als Stellvertreter" (Deputy-Union).
  deputy: number;
  unavailableAssignee: number;
}

/**
 * Compute the per-tab task counters. Mirrors the former inline `stats` object in Tasks.tsx 1:1.
 */
export function computeTaskStats(tasks: UnifiedTask[]): TaskStats {
  return {
    // TES-1994: isCountableTask statt !isExtraType — cancelled zählt in KEINEM Zähler
    // (Defense-in-Depth zum Fetch-Filter in useTasksQuery).
    total: tasks.filter((t) => isCountableTask(t)).length,
    openAll: tasks.filter((t) => {
      if (!isCountableTask(t)) return false;
      const status = getTaskStatus(t);
      const isNotStarted =
        !status.isCompleted && !status.isInProgress && !status.isAwaitingVerification;
      const isInProgress = status.isInProgress;
      const isAwaitingVerification = status.isAwaitingVerification;
      return isNotStarted || isInProgress || isAwaitingVerification;
    }).length,
    notStarted: tasks.filter(
      (t) =>
        isCountableTask(t) &&
        !getTaskStatus(t).isCompleted &&
        !getTaskStatus(t).isInProgress &&
        !getTaskStatus(t).isAwaitingVerification &&
        t.type !== "verification",
    ).length,
    in_progress: tasks.filter((t) => isCountableTask(t) && getTaskStatus(t).isInProgress).length,
    verification: tasks.filter((t) => isCountableTask(t) && getTaskStatus(t).isAwaitingVerification)
      .length,
    overdue: tasks.filter((t) => isCountableTask(t) && isTaskOverdue(t)).length,
    completed: tasks.filter((t) => isCountableTask(t) && getTaskStatus(t).isCompleted).length,
    // Copilot #773: identischer cancelled-Guard wie die Tab-Predicates — sonst zählte der
    // Stats-Badge cancelled, während der Tab sie ausblendet (Badge != Liste).
    // TES-2058 ([R12]): identisch zum TAB_PREDICATES.defects/inspections-Zweig — sonst würde der
    // KPI-Badge von der Tab-Liste abweichen, sobald qm_admin-Oversight-Zeilen im Spiel sind.
    defects: tasks.filter(
      (t) => (t.type === "defect" || t.type === "oversight_defect") && isCountableTask(t),
    ).length,
    inspections: tasks.filter(
      (t) => (t.type === "inspection" || t.type === "oversight_inspection") && isCountableTask(t),
    ).length,
    unassigned: tasks.filter(
      (t) => t.type === "unassigned_defect" || t.type === "unassigned_inspection",
    ).length,
    groupUnassigned: tasks.filter(
      (t) => t.type === "group_unassigned_defect" || t.type === "group_unassigned_inspection",
    ).length,
    pointsActions: tasks.filter((t) => t.type === "inspection_point" || t.type === "pt_action")
      .length,
    // TES-2053: zu-verifizierende Prüfungen (Verifizierer/Deputy) — eigener Tab-Zähler.
    inspectionVerification: tasks.filter((t) => t.type === "inspection_verification").length,
    // TES-2076: "Aufgaben als Stellvertreter" — reiner Typ-Match, deckungsgleich mit dem Tab-Predicate
    // (Badge == Listenlänge). Deputy-Tasks sind extra (isCountableTask=false), daher hier separat gezählt.
    deputy: tasks.filter((t) => t.type === "deputy").length,
    // Count unavailable_* types plus own tasks with unavailable assignees
    unavailableAssignee: getUnavailableAssigneeTasks(tasks).length,
  };
}

/**
 * Tasks with an unavailable assignee, used for bulk reassign. Mirrors the former inline
 * `unavailableAssigneeTasks` derivation in Tasks.tsx 1:1; also backs the
 * `unavailableAssignee` stat counter so the predicate lives in exactly one place.
 */
export function getUnavailableAssigneeTasks(tasks: UnifiedTask[]): UnifiedTask[] {
  return tasks.filter((t) => {
    // Include unavailable_* types
    if (t.type === "unavailable_defect" || t.type === "unavailable_inspection") return true;
    // Exclude unassigned types
    if (t.type === "unassigned_defect" || t.type === "unassigned_inspection") return false;
    // Include own tasks with unavailable assignees
    return isAssigneeUnavailable(t);
  });
}

// ---------------------------------------------------------------------------
// TES-2237 (US4, Goal-Gradient): "X von Y erledigt (diese Woche)" — weekly progress.
// ---------------------------------------------------------------------------

export interface WeekProgress {
  done: number;
  total: number;
}

/** Parses an ISO timestamp, returning null for missing/invalid values (never throws). */
function parseValidDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/**
 * Task types this metric counts. Own INSPECTIONS ONLY (TES-2237 US4 Copilot review, CHK PR #860):
 * `defectStatusMap` never marks a defect `isCompleted` in this dataset — `useTasksQuery`'s
 * defect fetch excludes `closed` (closed defects live on the Mängel-Seite, not "Meine Aufgaben"),
 * so a defect can never contribute to the numerator no matter how much work the assignee did.
 * Mixing defects into the denominator only (due-this-week, never completable here) would silently
 * cap the ratio for defect-heavy workloads — dishonest by omission. Scoping to inspections keeps
 * both sides of the ratio backed by a real, visible completion signal (`completed_at`).
 * `oversight_inspection` is deliberately EXCLUDED too (2nd Copilot pass): those rows are
 * tenant-wide qm_admin oversight items (Mig 411/TES-2058), not the caller's own work — counting
 * them would misrepresent "my week" with other people's pending inspections.
 */
const WEEK_PROGRESS_TASK_TYPES: ReadonlySet<TaskType> = new Set<TaskType>(["inspection"]);

/**
 * The ISO calendar week (Monday–Sunday) that `referenceDate` falls into. This week's
 * "workload" is: inspections already completed THIS week, plus inspections still open that are
 * DUE this week (see WEEK_PROGRESS_TASK_TYPES for why defects are out of scope here).
 */
export function computeWeekProgress(
  tasks: UnifiedTask[],
  referenceDate: Date = new Date(),
): WeekProgress {
  const weekStart = startOfWeek(referenceDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(referenceDate, { weekStartsOn: 1 });
  const inThisWeek = (date: Date) => isWithinInterval(date, { start: weekStart, end: weekEnd });

  let done = 0;
  let total = 0;

  for (const task of tasks) {
    if (!WEEK_PROGRESS_TASK_TYPES.has(task.type)) continue;
    if (!isCountableTask(task)) continue;
    const status = getTaskStatus(task);

    if (status.isCompleted) {
      const completedAt = parseValidDate(task.inspectionData?.completed_at ?? null);
      if (completedAt && inThisWeek(completedAt)) {
        done += 1;
        total += 1;
      }
      continue;
    }

    // Still open: counts toward this week's workload if it is DUE this week — a currently-open
    // inspection with no due date, or due outside this week, is not "this week's" work and stays
    // out of the denominator (avoids an always-huge, meaningless Y).
    const dueDate = parseValidDate(task.due_date);
    if (dueDate && inThisWeek(dueDate)) {
      total += 1;
    }
  }

  return { done, total };
}

/**
 * Visual bar width (0–100) for a done/total pair. Goal-Gradient (TES-2237 US4): "Erledigtes
 * sichtbar machen — nie bei 0 wenn etwas erledigt ist". A tiny done/total ratio would otherwise
 * round down to a visually-empty 0%-wide bar even though real progress exists — this floors the
 * rendered width (NOT the displayed percentage number, which stays honest) to `minVisiblePercent`
 * whenever done > 0.
 */
export function computeProgressBarWidth(
  done: number,
  total: number,
  minVisiblePercent = 4,
): number {
  if (total <= 0) return 0;
  const raw = Math.round((done / total) * 100);
  if (done > 0 && raw < minVisiblePercent) return minVisiblePercent;
  return Math.min(raw, 100);
}
